import { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import '../styles/restaurant-styles.css';
import { ProductContext } from './Context';
import { get, startCase, values, sortBy, isEqual } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { getMenuItemPrice, getPrice } from '../../initialize/managers/Currency';
import { DetailsLoading, TitleLoading } from '../../components/loading/Loading';
import { useLang } from '../../initialize/i18n/LangContext';
import { getTranslated, resolveShopLangs } from '../../initialize/i18n/lang';
import { LangDropdown } from '../../initialize/i18n/LangDropdown';

/* ── SVG icons ──────────────────────────────────────── */
const ChevLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 6-6 6 6 6" />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

/* ── Diet config ────────────────────────────────────── */
const DIET_LABELS: Record<string, { label: string; color: string }> = {
  v:  { label: 'Vegetarian', color: '#10B981' },
  vg: { label: 'Vegan',      color: '#059669' },
  gf: { label: 'Gluten-free',color: '#3B82F6' },
  s:  { label: 'Spicy',      color: '#EF4444' },
};

/* ── CDN base ───────────────────────────────────────── */
const CDN = 'https://dm6mmpk56rvc9.cloudfront.net/';

/* ══════════════════════════════════════════════════════
   MenuItemView — item detail (sheet-style full page)
══════════════════════════════════════════════════════ */
const MenuItemView = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { lang, setLang } = useLang();

  const {
    shop,
    loadingProduct,
    selectedProduct,
    fetchShop,
    fetchMenu,
    fetchProductForId,
  } = useContext(ProductContext);

  const [selectedOptions, setSelectedOptions] = useState<any>({});

  useEffect(() => {
    if (params?.shopId && params?.pid) {
      const shopId = params.shopId as string;
      const pid    = params.pid    as string;
      fetchShop(shopId);
      fetchMenu(shopId);
      fetchProductForId(shopId, pid);
    }
  }, [params, navigate]);

  const onSelectOption = (optionSetId: string, optionId: string) => {
    setSelectedOptions({ ...selectedOptions, [optionSetId]: optionId });
  };

  /* ── Price ──────────────────────────────────────── */
  const itemPrice = getMenuItemPrice(selectedProduct, selectedOptions);
  const price     = itemPrice ? getPrice({ price: itemPrice }) : null;

  /* ── Product meta ───────────────────────────────── */
  const heroUrl = selectedProduct?.menuImage
    ? `${CDN}${selectedProduct.menuImage}`
    : null;

  const diet: string[]    = selectedProduct?.dietary || selectedProduct?.diet || [];
  const hasVariants        = selectedProduct?.priceVariants?.length > 0;
  const sortedVariants     = hasVariants
    ? sortBy(selectedProduct.priceVariants, 'price')
    : [];

  const shopName: string  = get(shop, 'shopName', '');
  const shopLangs = resolveShopLangs(shop?.menuLanguages);

  return (
    <div className="dv-wrapper">
      <Helmet>
        <title>{`${startCase(selectedProduct?.productName || '')} — ${shopName} · Netlise`}</title>
        <meta
          name="description"
          content={`${selectedProduct?.description || ''} · ${shopName}`}
        />
        <meta property="og:type" content="article" />
      </Helmet>

      {/* ── Hero image ──────────────────────────────── */}
      <div className="dv-hero">
        {heroUrl && (
          <img
            src={heroUrl}
            alt={selectedProduct?.productName || 'Product image'}
          />
        )}

        {/* Floating back button */}
        <button
          className="dv-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ChevLeft />
        </button>

        {/* Language picker — mirrors back button on the right */}
        <LangDropdown
          lang={lang}
          setLang={setLang}
          langs={shopLangs}
          className="dv-lang-float"
        />
      </div>

      {/* ── Sheet content ───────────────────────────── */}
      <div className="dv-sheet">
        <div className="dv-sheet-drag" />

        <div className="dv-sheet-content">
          {loadingProduct ? (
            <>
              <DetailsLoading />
              <TitleLoading />
            </>
          ) : (
            <>
              {/* ── Title + price ──────────────────── */}
              <div className="dv-title-row">
                <div className="dv-title">
                  {lang === 'ru'
                    ? getTranslated(selectedProduct, 'productName', lang)
                    : startCase(selectedProduct?.productName || '')}
                </div>
                {price && (
                  <div className="dv-price">{price}</div>
                )}
              </div>

              {/* ── Description ───────────────────── */}
              {getTranslated(selectedProduct, 'description', lang) && (
                <div className="dv-description">
                  {getTranslated(selectedProduct, 'description', lang)}
                </div>
              )}

              {/* ── Dietary section ───────────────── */}
              {diet.length > 0 && (
                <div>
                  <div className="dv-section-label">Dietary</div>
                  <div className="dv-diet-row">
                    {diet.map((flag: string) => {
                      const d = DIET_LABELS[flag];
                      if (!d) return null;
                      return (
                        <span
                          key={flag}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '3px 8px',
                            borderRadius: 6,
                            fontSize: 10,
                            fontWeight: 600,
                            background: d.color + '14',
                            color: d.color,
                            letterSpacing: 0.3,
                            textTransform: 'uppercase',
                          }}
                        >
                          <span
                            style={{
                              width: 5,
                              height: 5,
                              borderRadius: '50%',
                              background: d.color,
                              display: 'inline-block',
                              flexShrink: 0,
                            }}
                          />
                          {d.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Size / price variants ─────────── */}
              {hasVariants && (
                <div className="dv-sizes-card">
                  <div className="dv-sizes-label">Sizes</div>
                  <div className="dv-sizes-grid">
                    {sortedVariants.map((variant: any) => {
                      const vid      = variant.sku || variant.id || variant.optionUnit;
                      const isActive = isEqual(
                        sortBy(values(selectedOptions)),
                        sortBy(variant.options || [])
                      );
                      return (
                        <div
                          key={vid}
                          className={`dv-size-tile${isActive ? ' selected' : ''}`}
                          onClick={() => {
                            /* select this variant */
                            if (variant.options) {
                              const newOpts: any = {};
                              (variant.options || []).forEach((optId: string) => {
                                newOpts[optId] = optId;
                              });
                              setSelectedOptions(newOpts);
                            }
                          }}
                        >
                          <div className="dv-size-name">
                            {variant.optionUnit || `Rs. ${Number(variant.price).toLocaleString()}`}
                          </div>
                          <div className="dv-size-price">
                            Rs. {Number(variant.price).toLocaleString()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Product options (modifier sets) ── */}
              {selectedProduct?.productOptions?.map(
                ({ id, options, setDisplayName }: any) => (
                  <div className="price-variants" key={id}>
                    <h3>{setDisplayName}</h3>
                    <div className="options">
                      {options.map((option: any) => (
                        <div
                          key={option.id}
                          onClick={() => onSelectOption(id, option.id)}
                          className={option.id === selectedOptions[id] ? 'selected' : ''}
                        >
                          {option.optionName}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}

              {/* ── Prep time info card ───────────── */}
              <div className="dv-prep-card">
                <ClockIcon />
                <div className="dv-prep-text">
                  Typical prep time 12–18 min. Let your server know if you have
                  any allergies.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuItemView;
